import React, { useState } from "react";
import { BoardMagnet } from "./BoardMagnet";
import { dayLabel } from "./boardUtils";

const useDrop = (onDrop) => {
  const [over, setOver] = useState(null);
  const props = (d) => ({
    onDragEnter: (e) => { e.preventDefault(); setOver(d); },
    onDragOver: (e) => { e.preventDefault(); setOver(d); },
    onDragLeave: () => setOver(null),
    onDrop: (e) => { e.preventDefault(); setOver(null); onDrop(e.dataTransfer.getData("text/plain"), d); },
  });
  return [over, props];
};

// Calendar month: Mon–Sat columns; stacks to a single column on phones.
export const MonthGrid = ({ dates, month, magnets, today, onMove, onOpen, onPickDay, showWho, selected, prefix = "board", selectedIds, selectMode }) => {
  const [over, dropProps] = useDrop(onMove);
  const [open, setOpen] = useState({});
  const byDay = magnets.reduce((a, m) => { (a[m.due_date] ||= []).push(m); return a; }, {});
  return (
    <div className="grid grid-cols-1 md:grid-cols-6 gap-1 rounded-xl border-2 border-slate-300 bg-slate-300 p-1" data-testid={`${prefix}-month-grid`}>
      {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d} className="hidden md:block text-center text-[11px] font-black uppercase text-white bg-slate-800 rounded py-1">{d}</div>)}
      {dates.map((d) => {
        const list = byDay[d] || [];
        const out = new Date(d + "T00:00:00").getMonth() !== month;
        const show = open[d] ? list : list.slice(0, 4);
        return (
          <div key={d} {...dropProps(d)} data-testid={`${prefix}-month-cell-${d}`}
            className={`min-h-[96px] rounded p-1 transition-colors ${over === d ? "bg-amber-100" : d === today ? "bg-amber-50" : out ? "bg-slate-100" : "bg-white"} ${selected === d ? "ring-2 ring-sky-500" : ""}`}>
            <button onClick={() => onPickDay && onPickDay(d)} data-testid={`${prefix}-month-day-${d}`} className={`w-full flex justify-between text-[11px] font-black mb-1 ${out ? "text-slate-400" : "text-slate-700"}`}>
              <span className="md:hidden">{dayLabel(d)}</span><span className="hidden md:inline">{Number(d.slice(8))}</span>
              {list.length > 0 && <span className="rounded-full bg-slate-800 text-white px-1.5 text-[9px] leading-4">{list.length}</span>}
            </button>
            <div className="space-y-1">
              {show.map((m) => <BoardMagnet key={m.id} m={m} onClick={onOpen} showWho={showWho} selectMode={selectMode} selected={selectedIds?.has(m.id)} />)}
              {list.length > 4 && <button onClick={() => setOpen((p) => ({ ...p, [d]: !p[d] }))} className="w-full text-[10px] font-bold text-sky-700 hover:underline">{open[d] ? "Show less" : `+${list.length - 4} more`}</button>}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Day / week strip for a single person: Overdue + day columns; stacks on phones.
export const DayColumns = ({ dates, magnets, today, onMove, onOpen, onPickDay, selected, prefix = "runsheet" }) => {
  const [over, dropProps] = useDrop(onMove);
  const [allOd, setAllOd] = useState(false);
  const cols = ["overdue", ...dates];
  return (
    <div className="grid grid-cols-1 gap-2 md:[grid-template-columns:var(--cols)]" style={{ "--cols": `repeat(${cols.length}, minmax(0, 1fr))` }} data-testid={`${prefix}-strip`}>
      {cols.map((d) => {
        const list = magnets.filter((m) => (d === "overdue" ? m.due_date < today : m.due_date === d));
        const od = d === "overdue";
        return (
          <div key={d} {...(od ? {} : dropProps(d))} data-testid={`${prefix}-col-${d}`}
            className={`rounded-xl border-2 min-h-[120px] transition-colors ${over === d ? "bg-amber-100 border-amber-400" : od ? "bg-red-50 border-red-200" : d === today ? "bg-amber-50 border-amber-300" : "bg-white border-slate-200"} ${selected === d ? "ring-2 ring-sky-500" : ""}`}>
            <button onClick={() => !od && onPickDay && onPickDay(d)} data-testid={`${prefix}-col-head-${d}`}
              className={`w-full flex justify-between items-center px-2 py-1.5 rounded-t-lg text-[11px] font-black uppercase ${od ? "bg-red-700 text-white" : d === today ? "bg-amber-400 text-slate-900" : "bg-slate-800 text-white"}`}>
              {od ? "Overdue" : dayLabel(d)}<span className="rounded-full bg-white/25 px-1.5">{list.length}</span>
            </button>
            <div className="p-1.5 space-y-1">
              {(od && !allOd ? list.slice(0, 5) : list).map((m) => <BoardMagnet key={m.id} m={m} overdue={od} onClick={onOpen} />)}
              {od && list.length > 5 && <button onClick={() => setAllOd((v) => !v)} data-testid={`${prefix}-overdue-more`} className="w-full text-[10px] font-bold text-red-700 hover:underline">{allOd ? "Show less" : `+${list.length - 5} more overdue — drag onto a day`}</button>}
            </div>
          </div>
        );
      })}
    </div>
  );
};
